import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

interface ColorSchemeConfig {
  cssVariables: Record<string, string>;
}

const DEFAULT_COLOR_SCHEME: ColorSchemeConfig = {
  cssVariables: {
    'brand-primary': '#FF7900',
    'brand-primary-rgb': '255, 121, 0',
    'brand-primary-active': '#cc6100',
    'brand-primary-light': 'rgba(255, 121, 0, 0.12)',
    'surface-dark': '#232323',
    'surface-darker': '#1e1e1e',
    'surface-mid': '#2a2a2a',
    'border-default': '#444444',
    'border-strong': '#333333',
    'text-primary': '#ffffff',
    'text-dark': '#000000',
    'text-secondary': '#b3b3b3',
    'text-muted': '#666666',
    'tooltip-bg': '#111111',
    'overlay-bg': 'rgba(0, 0, 0, 0.88)',
    'gray-font': 'rgb(141, 147, 160)',
    'pivot-bg': '#FF7900',
    'pivot-border': '#cc6100',
    'btn-secondary-bg': '#232323',
    'btn-secondary-border': '#232323',
  },
};

@Injectable({
  providedIn: 'root',
})
export class ColorSchemeService {
  private readonly colorSchemePath = 'assets/theme/color-scheme.json';

  constructor(private http: HttpClient) {}

  loadColorScheme(): Promise<void> {
    const envTheme = (environment as any).theme;

    if (envTheme?.colors) {
      this.applyColorScheme({ cssVariables: envTheme.colors });
      return Promise.resolve();
    }

    return firstValueFrom(this.http.get<ColorSchemeConfig>(this.colorSchemePath))
      .then((config) => this.applyColorScheme(config))
      .catch((error) => {
        console.warn('Color scheme could not be loaded, using defaults.', error);
        this.applyColorScheme(DEFAULT_COLOR_SCHEME);
      });
  }

  private applyColorScheme(config: ColorSchemeConfig): void {
    const root = document.documentElement;
    const cssVariables = config.cssVariables ?? {};

    Object.entries(cssVariables).forEach(([key, value]) => {
      root.style.setProperty(`--${key}`, value);
    });

    const brandPrimary = cssVariables['brand-primary'];
    if (brandPrimary) {
      root.style.setProperty('--bs-primary', brandPrimary);
      root.style.setProperty('--brand-primary', brandPrimary);
    }

    const brandPrimaryRgb =
      cssVariables['brand-primary-rgb'] ?? this.hexToRgb(brandPrimary);
    if (brandPrimaryRgb) {
      root.style.setProperty('--bs-primary-rgb', brandPrimaryRgb);
    }
  }

  private hexToRgb(hexColor?: string): string | null {
    if (!hexColor) {
      return null;
    }

    const normalizedHex = hexColor.replace('#', '').trim();
    const fullHex =
      normalizedHex.length === 3
        ? normalizedHex
            .split('')
            .map((char) => char + char)
            .join('')
        : normalizedHex;

    if (!/^[A-Fa-f0-9]{6}$/.test(fullHex)) {
      return null;
    }

    const red = Number.parseInt(fullHex.slice(0, 2), 16);
    const green = Number.parseInt(fullHex.slice(2, 4), 16);
    const blue = Number.parseInt(fullHex.slice(4, 6), 16);

    return `${red}, ${green}, ${blue}`;
  }
}
